import React from 'react';
import { Autocomplete, ListItem, ListItemText, TextField } from '@mui/material';
import { IUser } from '../Interfaces/IUser';
import { IUserGroup } from '../Interfaces/IUserGroup';

interface IOption {
  type: 'Mitarbeiter' | 'Gruppe';
  id: number;
  name: string;
}

interface IProps {
  users: IUser[];
  userGroups: IUserGroup[];
  idUser: number | null;
  idUserGroup: number | null;
  onChange: (sel: {
    idUser: number | null;
    idUserGroup: number | null;
  }) => void;
  label?: string;
  disabled?: boolean;
}

/**
 * Kombinierte Auswahl „Mitarbeiter ODER Gruppe" in EINEM Dropdown — dieselbe
 * Optik/Logik wie im Vorgang (Zuständigkeit). Setzt entweder idUser oder
 * idUserGroup (gegenseitig ausschließend).
 */
export const ResponsibleAutocomplete: React.FC<IProps> = ({
  users,
  userGroups,
  idUser,
  idUserGroup,
  onChange,
  label = 'Zuständige(r) Mitarbeiter / Gruppe',
  disabled = false,
}) => {
  const options: IOption[] = [
    ...(users ?? [])
      .filter(u => !u.isNotary && !u.isBlocked)
      .map(u => ({
        type: 'Mitarbeiter' as const,
        id: u.idUser,
        name: `${u.FirstName} ${u.LastName}`,
      })),
    ...(userGroups ?? []).map(g => ({
      type: 'Gruppe' as const,
      id: g.idUserGroup,
      name: g.UserGroup,
    })),
  ];

  const value =
    idUser != null
      ? (options.find(o => o.type === 'Mitarbeiter' && o.id === idUser) ?? null)
      : idUserGroup != null
        ? (options.find(o => o.type === 'Gruppe' && o.id === idUserGroup) ??
          null)
        : null;

  return (
    <Autocomplete<IOption>
      options={options}
      disabled={disabled}
      groupBy={o => o.type}
      getOptionLabel={o => o.name}
      isOptionEqualToValue={(o, v) => o.type === v.type && o.id === v.id}
      value={value}
      onChange={(_, newValue) => {
        if (!newValue) {
          onChange({ idUser: null, idUserGroup: null });
        } else if (newValue.type === 'Mitarbeiter') {
          onChange({ idUser: newValue.id, idUserGroup: null });
        } else {
          onChange({ idUser: null, idUserGroup: newValue.id });
        }
      }}
      renderOption={(props, option) => (
        <ListItem {...props} key={`${option.type}-${option.id}`}>
          <ListItemText primary={option.name} secondary={option.type} />
        </ListItem>
      )}
      renderInput={params => (
        <TextField {...params} label={label} size='small' />
      )}
    />
  );
};
